import { apiGetJson } from "@/lib/sm-api";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { createFileRoute, Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Settings, BarChart2, Plus, MessageSquare, Brain, Lightbulb, Zap, Target, Activity,
  LayoutDashboard, Bell, UserCheck, Users, Map, BookOpen, Flag, Network, Boxes, Inbox,
  BookMarked, Sparkles, Workflow, Store, ArrowLeft,
} from "lucide-react";
import { useState, useEffect, useMemo, type ComponentType } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { RENATA_NAV, GROUP_LABELS, type RenataNavItem } from "@/lib/renata/experience/nav";
import { renataChatSearch } from "@/lib/renata/experience/chat-search";
import { BUSINESS_LEXICON } from "@/lib/renata/experience/lexicon";

// Re-export so child routes can import from this file
export { GROUP_LABELS };

export const Route = createFileRoute("/app/renata")({
  component: RenataLayout,
  head: () => ({ meta: [{ title: "Renata — Structura Market" }] }),
});

/**
 * Merchant-facing Renata subnav — hide infrastructure surfaces.
 * Routes remain reachable by URL; they are not product nav.
 */
export const HIDDEN_MERCHANT_PATHS = [
  "/app/renata/agents",
  "/app/renata/collaboration",
  "/app/renata/memory",
  "/app/renata/packs",
] as const;

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  "/app/renata": LayoutDashboard,
  "/app/renata/chat": MessageSquare,
  "/app/renata/insights": Lightbulb,
  "/app/renata/timeline": Activity,
  "/app/renata/goals": Flag,
  "/app/renata/planning": Map,
  "/app/renata/missions": Target,
  "/app/renata/tasks": Zap,
  "/app/renata/playbooks": BookOpen,
  "/app/renata/push": Bell,
  "/app/renata/inbox": Inbox,
  "/app/renata/knowledge": BookMarked,
  "/app/renata/learning": Sparkles,
  "/app/renata/memory": Brain,
  "/app/renata/handoff": UserCheck,
  "/app/renata/agents": Users,
  "/app/renata/collaboration": Network,
  "/app/renata/packs": Boxes,
  "/app/renata/config": Settings,
  "/app/renata/store": Store,
  "/app/renata/usage": BarChart2,
  "/app/renata/automatizaciones": Workflow,
};

function isHiddenMerchantPath(to: string): boolean {
  return HIDDEN_MERCHANT_PATHS.some((p) => to === p || to.startsWith(`${p}/`));
}

function isNavActive(pathname: string, to: string): boolean {
  if (to === "/app/renata") {
    return pathname === "/app/renata" || pathname === "/app/renata";
  }
  return pathname.startsWith(to);
}

function resolveCurrentItem(pathname: string): RenataNavItem | undefined {
  const visible = RENATA_NAV.filter((t) => !isHiddenMerchantPath(t.to));
  const ranked = [...visible].sort((a, b) => b.to.length - a.to.length);
  return ranked.find((t) => isNavActive(pathname, t.to));
}

function RenataLayout() {
  const tenantId = useWorkspaceTenantId();
  const location = useLocation();
  const navigate = useNavigate();
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    void apiGetJson<{ ok?: boolean }>("/api/renata/conversations")
      .catch((e) => {
        const err = e as Error & { planLock?: PlanApiErrorPayload };
        if (err.planLock) setLock(err.planLock);
      });
  }, [tenantId]);

  const current = useMemo(() => resolveCurrentItem(location.pathname), [location.pathname]);

  const isChat = location.pathname.startsWith("/app/renata/chat");
  const isHome = location.pathname === "/app/renata" || location.pathname === "/app/renata/";

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col",
        isChat ? "h-full min-h-0 overflow-hidden" : "min-w-0",
      )}
      data-renata-shell="contextual"
    >
      <header className="renata-context-nav shrink-0 border-b bg-background px-3 py-2 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          {!isHome ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 gap-1.5 px-2 text-muted-foreground"
              onClick={() => {
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  void navigate({ to: "/app/renata" });
                }
              }}
              aria-label="Volver"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Renata</span>
            </Button>
          ) : null}

          <div className="min-w-0 flex-1">
            {isHome ? (
              <p className="truncate text-sm font-semibold tracking-tight">Renata</p>
            ) : (
              <>
                <p className="truncate text-[11px] text-muted-foreground">
                  {current ? GROUP_LABELS[current.group] : "Renata"}
                </p>
                <p className="truncate text-sm font-semibold tracking-tight">
                  {current?.label ?? ""}
                </p>
              </>
            )}
          </div>

          <Button asChild size="sm" variant="default" className="h-8 shrink-0 gap-1.5">
            <Link to="/app/renata/chat" search={renataChatSearch()}>
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Conversar</span>
            </Link>
          </Button>
        </div>
      </header>

      <div
        className={cn(
          "min-w-0 flex-1",
          isChat ? "flex min-h-0 flex-col overflow-hidden" : "min-w-0",
        )}
        data-renata-content
      >
        <Outlet />
      </div>
    </div>
  );
}
